import React, { useState, useEffect } from 'react';
import { notificationsApi } from '../api';
import { LoadingSpinner } from './LoadingSpinner';
import { ErrorMessage } from './ErrorMessage';

interface Notification {
  notification_id: string;
  notification_type: string;
  subject: string;
  message: string;
  status: string;
  sent_at: string;
  read_at: string | null;
  created_at: string;
}

export const NotificationCenter: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, [page]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsApi.getAll({ page, limit: 10, channel: 'in_app' });
      if (response.success && response.data) {
        setNotifications(response.data);
        setTotalPages(response.pagination?.totalPages || 1);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationsApi.getUnreadCount();
      if (response.success && response.data) {
        setUnreadCount(response.data.unread_count);
      }
    } catch (err: any) {
      // Silently fail for unread count
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationsApi.markAsRead(notificationId);

      // Update local state
      setNotifications(
        notifications.map((n) =>
          n.notification_id === notificationId
            ? { ...n, read_at: new Date().toISOString() }
            : n
        )
      );
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (err: any) {
      setError(err.message || 'Failed to mark notification as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();

      // Update local state
      setNotifications(
        notifications.map((n) =>
          n.read_at ? n : { ...n, read_at: new Date().toISOString() }
        )
      );
      setUnreadCount(0);
    } catch (err: any) {
      setError(err.message || 'Failed to mark all notifications as read');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'application_submitted':
        return '📄';
      case 'application_approved':
        return '✅';
      case 'application_declined':
        return '❌';
      case 'loan_disbursed':
        return '💰';
      case 'payment_received':
        return '💳';
      case 'payment_reminder':
        return '⏰';
      case 'payment_overdue':
        return '⚠️';
      default:
        return '📬';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading && notifications.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="notification-center">
      <div className="notification-center-header">
        <h3>Notifications</h3>
        <div className="notification-actions">
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount} unread</span>
          )}
          {notifications.some((n) => !n.read_at) && (
            <button onClick={handleMarkAllAsRead} className="btn btn-sm btn-secondary">
              Mark all as read
            </button>
          )}
        </div>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      <div className="notifications-list">
        {notifications.length === 0 ? (
          <div className="empty-state">
            <p>No notifications yet</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.notification_id}
              className={`notification-item ${!notification.read_at ? 'unread' : ''}`}
              onClick={() => !notification.read_at && handleMarkAsRead(notification.notification_id)}
            >
              <div className="notification-icon">
                {getNotificationIcon(notification.notification_type)}
              </div>
              <div className="notification-content">
                <div className="notification-header">
                  <span className="notification-subject">{notification.subject}</span>
                  <span className="notification-time">{formatDate(notification.created_at)}</span>
                </div>
                <p className="notification-message">{notification.message}</p>
              </div>
              {!notification.read_at && <div className="unread-indicator"></div>}
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="btn btn-sm btn-secondary"
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
            className="btn btn-sm btn-secondary"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};
